import React from 'react';

// Footer pager for the dashboard's full-width tables. It sits INSIDE the table
// surface (directly under the last row) so it reads as part of the table rather
// than as a widget floating beneath it.
//
// Purely presentational: the caller owns the page state and the refetch. `total`
// and `pageCount` must come from the paginated endpoint, not from the rows on
// screen — that is the whole point of the range text ("21–40 of 137" is honest
// about the rows this client never received).

const s = {
  bar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
    flexWrap: 'wrap', padding: '10px 16px',
    borderTop: '1px solid var(--border, #e2e8f0)', background: '#f8fafc',
  },
  range: { fontSize: '12px', color: 'var(--text-secondary, #64748b)' },
  controls: { display: 'flex', alignItems: 'center', gap: '8px' },
  btn: (disabled) => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '5px 10px', borderRadius: 'var(--radius, 6px)',
    border: '1px solid var(--border, #e2e8f0)', background: '#fff',
    fontSize: '12px', fontWeight: 600,
    color: disabled ? 'var(--text-muted, #94a3b8)' : 'var(--text-secondary, #475569)',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  }),
  page: { fontSize: '12px', color: 'var(--text-secondary, #64748b)', whiteSpace: 'nowrap', padding: '0 2px' },
  sizeWrap: { display: 'flex', alignItems: 'center', gap: '6px' },
  sizeSelect: {
    height: '28px', padding: '0 6px', borderRadius: 'var(--radius, 6px)',
    border: '1px solid var(--border, #e2e8f0)', background: '#fff',
    fontSize: '12px', color: 'var(--text-secondary, #475569)', cursor: 'pointer',
  },
};

export default function Pagination({
  page = 1,
  pageCount = 1,
  total = 0,
  limit = 20,
  onPageChange,
  // Omit to hide the rows-per-page control.
  onLimitChange,
  pageSizes = [10, 20, 50, 100],
  busy = false,
  noun = 'row',
  label = 'Pagination',
}) {
  if (!total) return null;

  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);
  const canPrev = page > 1 && !busy;
  const canNext = page < pageCount && !busy;

  return (
    <nav style={s.bar} aria-label={label}>
      <span style={s.range}>
        {first}–{last} of {total} {noun}{total === 1 ? '' : 's'}
      </span>

      <div style={s.controls}>
        {onLimitChange && (
          <span style={s.sizeWrap}>
            <span style={s.range}>Rows</span>
            <select
              style={s.sizeSelect}
              value={String(limit)}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              disabled={busy}
              aria-label={`${noun}s per page`}
            >
              {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        )}
        <button
          type="button"
          style={s.btn(!canPrev)}
          disabled={!canPrev}
          onClick={() => canPrev && onPageChange(page - 1)}
        >
          ‹ Previous
        </button>
        <span style={s.page}>Page {page} of {Math.max(pageCount, 1)}</span>
        <button
          type="button"
          style={s.btn(!canNext)}
          disabled={!canNext}
          onClick={() => canNext && onPageChange(page + 1)}
        >
          Next ›
        </button>
      </div>
    </nav>
  );
}
